import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import useCurrentJobOrStep from "~/components/build/Show/lib/useCurrentJobOrStep";
import { CommandJob } from "~/components/build/Show/lib/types";
import { Job } from "~/stores/JobLogStore";
import { testFailuresQueryOptions } from "~/components/build/Show/lib/queries/testFailures";
import { matchFailureMarkers } from "~/components/job/Job/Log/matchFailureMarkers";

export type { TestFailure, FailureMarker } from "~/components/job/Job/Log/matchFailureMarkers";
export {
  expandedLineCount,
  matchFailureMarkers,
} from "~/components/job/Job/Log/matchFailureMarkers";

/**
 * Returns true when the feature flag is enabled and the job has
 * finished with Test Engine data available.
 */
export function shouldFetchTestFailureMarkers(job: CommandJob | null | undefined): boolean {
  return (
    Features.TestEngineLogFailureMarkers &&
    job?.passed === false &&
    Boolean(job?.testEngine?.testTabUrls?.tabUrl)
  );
}

/**
 * Fetches test failure data for a failed job and matches failure text
 * against log line content to produce multi-line gutter markers.
 *
 * Uses React Query so the data can be prefetched when the job drawer
 * opens, before the Log tab is rendered.
 */
export function useTestFailureMarkers(log: Job) {
  const { build } = useBuild();
  const { currentLatestJob } = useCurrentJobOrStep();

  const job =
    currentLatestJob && currentLatestJob.type === "script"
      ? (currentLatestJob as CommandJob)
      : null;

  const shouldFetch = shouldFetchTestFailureMarkers(job);

  const { data, isLoading } = useQuery({
    ...testFailuresQueryOptions(build, log.id),
    enabled: shouldFetch && Boolean(log.id),
  });

  const failures = data?.failures ?? [];

  const markers = useMemo(() => matchFailureMarkers(failures, log.groups), [failures, log.groups]);

  return { markers, loading: isLoading && shouldFetch };
}
